import { makeAutoObservable } from "mobx";
import { MapStore } from "@/components/Map/stores/MapStore";
import { getStructureTypeByName } from "@/utils/helpers";
import { AsyncStateStore } from "@/utils/AsyncStateStore";
import { structure } from "@/endpoints/dryp/structure";
import { blockage } from "@/endpoints/dryp/blockage";
import { structuresLayerConfig, structuresDataConfig } from "@/pages/Dashboard/Structures";
import { blockageLayerConfig, blockageDataConfig } from "@/pages/Dashboard/Blockages";
import {
	getColorForIAndIMarker,
	iAndILayerConfig,
	iAndIDataConfig,
	type IAndIMapItem,
} from "@/components/IAndI/IAndIList";
import { type IAndITimePeriodsValue } from "@/config/date";
import { CUSTOM_FILTERS } from "@/config/list";
import { getTableStructureFilterParams, StructureFilterParams } from "@/utils/filters";
import { infiltrationAndInflow } from "@/endpoints/dryp/infiltrationAndInflow";

export type Perspective = "overview" | "IAndI" | "blockages";

const DEFAULT_TIME_PERIOD: IAndITimePeriodsValue = "last90d";
const DEFAULT_PERSPECTIVE: Perspective = "overview";

export class GeneralMapStore {
	mapStore: MapStore<any> = new MapStore<any>(
		structuresLayerConfig,
		structuresDataConfig,
		{
			clusterRadius: 30,
			clusterMaxZoom: 11,
		},
		() => this.getStructuresSilently(),
	);

	allStructures = new AsyncStateStore(structure.getAllStructures);
	iAndIData = new AsyncStateStore(infiltrationAndInflow.getSummaries);
	blockageData = new AsyncStateStore(blockage.getAll);
	structureTypes = new AsyncStateStore(structure.getAllStructureTypes);
	structureGroups = new AsyncStateStore(structure.getAllStructureGroups);

	constructor() {
		makeAutoObservable(this);
		this.structureTypes.fetch({ pageNumber: 0, pageSize: 2000 });
		this.structureGroups.fetch({ pageNumber: 0, pageSize: 2000 });
		this.allStructures.fetchIfEmpty({ pageNumber: 0, pageSize: 20000 });
	}

	// Perspective is stored inside mapStore.filters so it round-trips through the
	// existing URL sync (useUrlQuerySync in <Map>) without any custom plumbing.
	// Note: useUrlQuerySync's parser normalises FILTERS_STRING values to arrays
	// on read-back from the URL, so we accept either string or [string].
	get perspective(): Perspective {
		const raw = this.mapStore.filters.getFilter(CUSTOM_FILTERS.perspective);
		const value = Array.isArray(raw) ? raw[0] : raw;
		if (value === "overview" || value === "IAndI" || value === "blockages") return value;
		return DEFAULT_PERSPECTIVE;
	}

	onPerspectiveChange = (perspective: Perspective) => {
		if (perspective === "overview") {
			this.onClickOverview();
		} else if (perspective === "IAndI") {
			this.onClickIAndI();
		} else if (perspective === "blockages") {
			this.onClickBlockages();
		}
	};

	get loading() {
		return this.structureTypes.loading || this.structureGroups.loading;
	}

	// Heavy overlay (covers the map) — initial structures fetch only.
	get isLoading() {
		return this.allStructures.loading;
	}

	// Inline indicators next to the perspective tabs — perspective-specific
	// refreshes (e.g. time-period change, filter change) shouldn't block the map.
	get isIAndILoading() {
		return this.iAndIData.loading;
	}

	get isBlockagesLoading() {
		return this.blockageData.loading;
	}

	// I&I time period. Stored on mapStore.filters so it participates in the
	// existing filters.snapshot reaction → refetches I&I data — and round-trips
	// with the URL via useUrlQuerySync (FILTERS_STRING includes timePeriod).
	get timePeriod(): IAndITimePeriodsValue {
		const raw = this.mapStore.filters.getFilter(CUSTOM_FILTERS.timePeriod);
		const value = Array.isArray(raw) ? raw[0] : raw;
		return (value as IAndITimePeriodsValue | undefined) ?? DEFAULT_TIME_PERIOD;
	}

	setTimePeriod = (value: IAndITimePeriodsValue) => {
		this.mapStore.filters.setFilter(CUSTOM_FILTERS.timePeriod, value);
	};

	// Structures filtered down to the active perspective, used for the sidebar's
	// per-type / per-group counts. On overview: all structures. On I&I / blockages:
	// only structures that have the relevant monitoring data.
	get viewPerspectiveStructures(): Dryp.Structure[] {
		const structures = this.allStructures.data?.items ?? [];
		if (this.perspective === "IAndI") {
			return structures.filter(structureItem => structureItem.hasIAndI);
		}
		if (this.perspective === "blockages") {
			return structures.filter(structureItem => structureItem.hasBlockage);
		}
		return structures;
	}

	get filteredStructures(): Dryp.Structure[] {
		return this.filterStructures(this.viewPerspectiveStructures);
	}

	onClickOverview = async () => {
		this.mapStore.filters.setFilter(CUSTOM_FILTERS.perspective, "overview");
		this.mapStore.filters.clear(CUSTOM_FILTERS.timePeriod);
		this.mapStore.reset({ layerConfig: structuresLayerConfig, dataConfig: structuresDataConfig });

		// Structures are fetched once (no filter params) and reused across filter
		// changes. Filter selections narrow the markers client-side. Sidebar counts
		// always read from the full, unfiltered structures list — matching the
		// original page map's behaviour.
		const fetchOverview = async () => {
			if (!this.mapStore.isLoaded) return;
			await this.allStructures.fetchIfEmpty({ pageNumber: 0, pageSize: 20000 });
			await this.mapStore.connectionsStore.connectionsApi.fetchIfEmpty({ pageNumber: 0, pageSize: 2000 });
			const filtered = this.filterStructures(this.allStructures.data?.items ?? []);
			this.mapStore.setMarkersAndFit(filtered);
			this.updateMissingConnectivity(filtered);
		};

		this.mapStore.onFetch = fetchOverview;
		await fetchOverview();
	};

	onClickIAndI = async () => {
		this.mapStore.filters.setFilter(CUSTOM_FILTERS.perspective, "IAndI");
		this.mapStore.reset({ layerConfig: iAndILayerConfig, dataConfig: iAndIDataConfig });
		this.mapStore.connectionsStore.setMissingConnectivity([]);

		const fetchIAndI = async () => {
			if (!this.mapStore.isLoaded) return;
			await this.iAndIData.fetch(
				{ pageNumber: 0, pageSize: 100000 },
				this.timePeriod,
				undefined,
				this.getStructureFilterParams(),
			);
			// const stats = this.iAndIData.data?.itemsMeta?.iAndIPerPipeLength;
			const iAndIRecords = (this.iAndIData.data?.items ?? []).map<IAndIMapItem>(iAndIRecord => ({
				...iAndIRecord,
				markerColor: getColorForIAndIMarker(),
				// iAndIRecord.iAndIPerPipeLength?.value,
				// stats && stats.max != null ? { min: stats.min ?? 0, max: stats.max } : undefined,
			}));
			this.mapStore.setMarkersAndFit(iAndIRecords);
		};

		this.mapStore.onFetch = fetchIAndI;
		await fetchIAndI();
	};

	onClickBlockages = async () => {
		this.mapStore.filters.setFilter(CUSTOM_FILTERS.perspective, "blockages");
		this.mapStore.filters.clear(CUSTOM_FILTERS.timePeriod);
		this.mapStore.reset({ layerConfig: blockageLayerConfig, dataConfig: blockageDataConfig });
		this.mapStore.connectionsStore.setMissingConnectivity([]);

		const fetchBlockages = async () => {
			if (!this.mapStore.isLoaded) return;
			await this.blockageData.fetch(
				{ pageNumber: 0, pageSize: 100000 },
				undefined,
				this.getStructureFilterParams(),
				undefined,
			);
			this.mapStore.setMarkersAndFit(this.blockageData.data?.items ?? []);
		};

		this.mapStore.onFetch = fetchBlockages;
		await fetchBlockages();
	};

	private searchTerm = (): string =>
		String((this.mapStore.filters.getFilter(CUSTOM_FILTERS.search) as string | undefined) ?? "")
			.trim()
			.toLowerCase();

	private matchesSearch = (...names: (string | null | undefined)[]): boolean => {
		const search = this.searchTerm();
		if (!search) return true;
		return names.some(name => (name?.toLowerCase() ?? "").includes(search));
	};

	// Client-side filter for overview markers. Sidebar counts use the unfiltered
	// structures list (see viewPerspectiveStructures), so toggling chips here
	// changes what's on the map without affecting the sidebar counts.
	private filterStructures = (structures: Dryp.Structure[]): Dryp.Structure[] => {
		const { filters } = this.mapStore;
		const activeValue = (filters.getFilter(CUSTOM_FILTERS.isActive) as string | undefined) ?? "active";
		const selectedTypeIds = filters.selectedIds(CUSTOM_FILTERS.structureTypesIds);
		const selectedGroupIds = filters.selectedIds(CUSTOM_FILTERS.structureGroupIds);
		const selectedNetworkTypes = filters.selectedIds(CUSTOM_FILTERS.networkType);

		if (
			activeValue === "all" &&
			selectedTypeIds.size === 0 &&
			selectedGroupIds.size === 0 &&
			selectedNetworkTypes.size === 0 &&
			!this.searchTerm()
		) {
			return structures;
		}

		return structures.filter(structureItem => {
			if (activeValue === "active" && !structureItem.isActive) return false;
			if (activeValue === "inactive" && structureItem.isActive) return false;
			if (selectedTypeIds.size > 0) {
				if (!structureItem.type || !selectedTypeIds.has(String(structureItem.type.id))) return false;
			}
			if (selectedGroupIds.size > 0) {
				const matchesGroup = structureItem.groups?.some(groupItem =>
					selectedGroupIds.has(String(groupItem.id)),
				);
				if (!matchesGroup) return false;
			}
			if (selectedNetworkTypes.size > 0) {
				if (!structureItem.networkType || !selectedNetworkTypes.has(String(structureItem.networkType))) {
					return false;
				}
			}
			if (!this.matchesSearch(structureItem.name, structureItem.description)) return false;
			return true;
		});
	};

	getStructuresSilently = async () => {
		await this.allStructures.silentFetch();
		const structures = this.filterStructures(this.allStructures.data?.items ?? []);
		this.mapStore.setMarkers(structures);
		this.updateMissingConnectivity(structures);
	};

	isMissingConnectivity = (structureItem: Dryp.Structure): boolean => {
		const manholeTypeId = String(getStructureTypeByName("manhole").id);
		if (String(structureItem.type?.id) !== manholeTypeId) return false;
		return !structureItem.hasConnectivity;
	};

	private updateMissingConnectivity = (structures: Dryp.Structure[]) => {
		const missingIds = structures
			.filter(structureItem => this.isMissingConnectivity(structureItem))
			.map(structureItem => String(structureItem.id));
		this.mapStore.connectionsStore.setMissingConnectivity(missingIds);
	};

	private getStructureFilterParams = (): StructureFilterParams =>
		getTableStructureFilterParams(this.mapStore.filters.getFilters());
}
